import { useEffect, useRef } from 'react';
import fetchWithDefaults from '../../CommonUtils/fetchWithDefaults';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import {
  MGP_WARMUP_POLL_INTERVAL_MS,
  MGP_WARMUP_STATUS_MAX_RETRIES,
  MGP_WARMUP_TIMEOUT_MS,
} from '../MoneyGuideProConstants';
import { MgpWarmupStatusResponse } from '../MoneyGuideProTypes';

const fetchWarmupStatus = async (clientId: string): Promise<MgpWarmupStatusResponse | null> => {
  const response = await fetchWithDefaults(
    urlWithParams('/Consumer/MgpReport/WarmupStatus', { clientId }),
  );

  if (!response.ok) {
    return null;
  }

  const {
    success,
    payload: { viewData },
  }: { success: boolean; payload: { viewData: MgpWarmupStatusResponse } } = await response.json();

  if (!success) {
    return null;
  }

  return viewData;
};

export const useMgpWarmupPolling = (
  clientId: string,
  enabled: boolean,
  onComplete: () => void,
) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    let consecutiveFailures = 0;
    const startedAt = Date.now();

    const complete = () => {
      if (!cancelled) {
        onCompleteRef.current();
      }
    };

    const scheduleNextPoll = () => {
      if (cancelled) {
        return;
      }

      if (Date.now() - startedAt >= MGP_WARMUP_TIMEOUT_MS) {
        complete();
        return;
      }

      timeoutId = window.setTimeout(poll, MGP_WARMUP_POLL_INTERVAL_MS);
    };

    const poll = async () => {
      if (cancelled) {
        return;
      }

      if (Date.now() - startedAt >= MGP_WARMUP_TIMEOUT_MS) {
        complete();
        return;
      }

      const handleFetchFailure = () => {
        consecutiveFailures += 1;

        if (consecutiveFailures >= MGP_WARMUP_STATUS_MAX_RETRIES) {
          complete();
          return;
        }

        scheduleNextPoll();
      };

      try {
        const status = await fetchWarmupStatus(clientId);

        if (cancelled) {
          return;
        }

        if (!status) {
          handleFetchFailure();
          return;
        }

        consecutiveFailures = 0;

        if (!status.warmupInProgress || Date.now() - startedAt >= MGP_WARMUP_TIMEOUT_MS) {
          complete();
          return;
        }

        scheduleNextPoll();
      } catch {
        if (cancelled) {
          return;
        }

        handleFetchFailure();
      }
    };

    poll();

    return () => {
      cancelled = true;

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [clientId, enabled]);
};
